import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, XCircle, Trash2, TrendingUp, Users, Wifi, Activity, Download, Edit, Mail, Copy, FileText, Shield, MapPin, Clock, History, X, Upload, Smartphone, RefreshCw, Lock } from 'lucide-react';
import { authAPI, adminAPI } from '../services/api';
import type { AdminStats, RegistrationCode, LifecycleSummary, EmailTemplateSettings, QuicBlockingStatus, MemberLoginHistory, GeoLocation } from '../types';
import { getVersionString } from '../version';

export default function MemberManagement() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [codes, setCodes] = useState<RegistrationCode[]>([]);
  const [lifecycle, setLifecycle] = useState<LifecycleSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState<'sasadmin' | 'sys-operator'>('sasadmin');
  const isSasadmin = role === 'sasadmin';
  const isReadOnly = !isSasadmin;
  const [refreshing, setRefreshing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [notes, setNotes] = useState('');
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editingCode, setEditingCode] = useState<RegistrationCode | null>(null);
  const [editForm, setEditForm] = useState({ memberName: '', memberEmail: '', memberLanguage: 'ja', notes: '' });
  const [updating, setUpdating] = useState(false);
  const [copySuccess, setCopySuccess] = useState<string | null>(null);
  const [showTemplateSettings, setShowTemplateSettings] = useState(false);
  const [emailTemplate, setEmailTemplate] = useState<EmailTemplateSettings>({
    emailTitle: 'SAS member activation letter',
    androidApkUrl: '',
    androidDiawiUrl: '',
    iosAppstoreUrl: '',
    androidPlaystoreUrl: '',
  });
  const [templateForm, setTemplateForm] = useState<EmailTemplateSettings>({
    emailTitle: 'SAS member activation letter',
    androidApkUrl: '',
    androidDiawiUrl: '',
    iosAppstoreUrl: '',
    androidPlaystoreUrl: '',
  });
  const [quicBlocking, setQuicBlocking] = useState<QuicBlockingStatus>({ enabled: false });
  const [quicUpdating, setQuicUpdating] = useState(false);
  const [loginHistoryCode, setLoginHistoryCode] = useState<string | null>(null);
  const [loginHistory, setLoginHistory] = useState<MemberLoginHistory[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [apkList, setApkList] = useState<any[]>([]);
  const [apkUploading, setApkUploading] = useState(false);
  const [apkVersion, setApkVersion] = useState('');
  const [apkNotes, setApkNotes] = useState('');
  const [codesPage, setCodesPage] = useState(0);
  const [codesSearch, setCodesSearch] = useState('');
  const [expandedApkId, setExpandedApkId] = useState<number | null>(null);
  const [dailyDate, setDailyDate] = useState<string>(() => {
    // Default = yesterday in Asia/Tokyo (the natural "what happened yesterday" download)
    const nowJstMs = Date.now() + 9 * 3600 * 1000;
    return new Date(nowJstMs - 86400000).toISOString().split('T')[0];
  });
  const [downloadingDailyCsv, setDownloadingDailyCsv] = useState(false);

  useEffect(() => {
    loadDashboard();
  }, []);

  async function loadDashboard() {
    try {
      const authCheck = await authAPI.checkAdminAuth();
      if (!authCheck.authenticated) {
        navigate('/admin');
        return;
      }
      const currentRole = (authCheck.user?.role ?? 'sasadmin') as 'sasadmin' | 'sys-operator';
      setRole(currentRole);
      const sasadmin = currentRole === 'sasadmin';

      // sys-operator: codes (PII-masked) + lifecycle now allowed by backend.
      // /email-template, /quic-blocking remain sasadmin-only.
      const [statsData, codesData, templateData, quicData, lifecycleData] = await Promise.all([
        adminAPI.getStats().catch(() => null),
        adminAPI.getCodes().catch(() => [] as RegistrationCode[]),
        sasadmin ? adminAPI.getEmailTemplate().catch(() => null) : Promise.resolve(null),
        sasadmin ? adminAPI.getQuicBlocking().catch(() => ({ enabled: false } as QuicBlockingStatus)) : Promise.resolve({ enabled: false } as QuicBlockingStatus),
        adminAPI.getLifecycleSummary().catch(() => null),
      ]);

      if (statsData) setStats(statsData);
      setCodes(codesData);
      if (templateData) setEmailTemplate(templateData);
      setQuicBlocking(quicData);
      setLifecycle(lifecycleData);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  async function handleRefresh() {
    setRefreshing(true);
    await loadDashboard();
  }

  const [memberName, setMemberName] = useState('');
  const [memberEmail, setMemberEmail] = useState('');

  async function handleGenerateCode() {
    setGenerating(true);
    try {
      await adminAPI.generateCode({
        notes: notes || undefined,
        memberName: memberName || undefined,
        memberEmail: memberEmail || undefined,
      });
      setNotes('');
      setMemberName('');
      setMemberEmail('');
      await loadDashboard();
    } catch (error: any) {
      alert(error.message || t('dashboard.codeGenFailed'));
    } finally {
      setGenerating(false);
    }
  }

  async function handleRevokeCode(code: string) {
    if (!confirm(t('dashboard.confirmRevoke', { code }))) return;

    try {
      await adminAPI.revokeCode(code);
      await loadDashboard();
    } catch (error: any) {
      alert(error.message || t('dashboard.revokeFailed'));
    }
  }

  async function handleDeleteCode(code: string) {
    if (!confirm(t('dashboard.confirmDelete', { code }))) return;

    try {
      await adminAPI.deleteCode(code);
      await loadDashboard();
    } catch (error: any) {
      alert(error.message || t('dashboard.deleteFailed'));
    }
  }

  function handleEditClick(code: RegistrationCode) {
    setEditingCode(code);
    setEditForm({
      memberName: code.memberName || '',
      memberEmail: code.memberEmail || '',
      memberLanguage: code.memberLanguage || 'ja',
      notes: code.notes || '',
    });
  }

  async function handleUpdateCode() {
    if (!editingCode) return;

    setUpdating(true);
    try {
      await adminAPI.updateCode(editingCode.code, editForm);
      setEditingCode(null);
      await loadDashboard();
    } catch (error: any) {
      alert(error.message || '更新に失敗しました');
    } finally {
      setUpdating(false);
    }
  }

  function handleCopyEmail(code: RegistrationCode) {
    const androidSection = emailTemplate.androidPlaystoreUrl
      ? `Android:
${emailTemplate.androidApkUrl}
Or: ${emailTemplate.androidDiawiUrl}
Or Play Store: ${emailTemplate.androidPlaystoreUrl}`
      : `Android:
${emailTemplate.androidApkUrl}
Or: ${emailTemplate.androidDiawiUrl}`;

    const emailContent = `Subject: ${emailTemplate.emailTitle}

Dear ${code.memberName || 'User'},

1) App store:
${androidSection}

iPhone:
${emailTemplate.iosAppstoreUrl}

2) Member code:
${code.code}

3) SAS system:
https://sas.dev.miosys.jp/

Best regards,

SAS Admin.`;

    navigator.clipboard.writeText(emailContent).then(() => {
      setCopySuccess(code.code);
      setTimeout(() => setCopySuccess(null), 2000);
    }).catch(() => {
      alert('クリップボードへのコピーに失敗しました');
    });
  }

  function handleTemplateSettingsClick() {
    setTemplateForm({...emailTemplate});
    setShowTemplateSettings(true);
    loadApkList();
  }

  async function loadApkList() {
    try {
      const result = await adminAPI.getApkList();
      const items = result.items || [];
      setApkList(items);
      // Auto-expand the latest (first) item
      if (items.length > 0) {
        setExpandedApkId(items[0].id);
        // Default upload version = latest APK version (not web version)
        if (!apkVersion) {
          setApkVersion(items[0].version);
        }
      }
    } catch (error) {
      console.error('Failed to load APK list:', error);
    }
  }

  async function handleApkUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.apk')) {
      alert('APK (.apk) ファイルのみアップロードできます');
      return;
    }

    setApkUploading(true);
    try {
      const result = await adminAPI.uploadApk(file, apkVersion, apkNotes);
      // Auto-update the APK URL field in template form
      if (result.data.downloadUrl) {
        setTemplateForm(prev => ({ ...prev, androidApkUrl: result.data.downloadUrl }));
      }
      alert('APK アップロード完了');
      // Reload the list and expand the new one
      await loadApkList();
      setApkNotes('');
    } catch (error: any) {
      alert(error.message || 'APKアップロードに失敗しました');
    } finally {
      setApkUploading(false);
      e.target.value = '';
    }
  }

  async function handleDeleteApk(id: number, version: string) {
    if (!confirm(`Delete APK ${version} (ID: ${id})? This cannot be undone.`)) return;
    try {
      await adminAPI.deleteApk(id);
      await loadApkList();
    } catch (error: any) {
      alert(error.message || 'Failed to delete APK');
    }
  }

  async function handleUpdateTemplate() {
    setUpdating(true);
    try {
      const result = await adminAPI.updateEmailTemplate(templateForm);
      setEmailTemplate(result.data);
      setShowTemplateSettings(false);
    } catch (error: any) {
      alert(error.message || 'テンプレート更新に失敗しました');
    } finally {
      setUpdating(false);
    }
  }

  async function handleToggleQuicBlocking() {
    if (quicUpdating) return;

    const action = quicBlocking.enabled ? t('dashboard.quicDisableAction') : t('dashboard.quicEnableAction');
    const confirmMessage = quicBlocking.enabled
      ? t('dashboard.quicConfirmDisable')
      : 'QUIC ブロッキングを有効化しますか？\n\n有効化すると:\n• 全デバイス (Android/iPhone) はHTTPS/TCPを使用します (mitmproxyでキャプチャ可能)\n• ネイティブアプリ (YouTube app等) は動作しなくなります\n• ユーザーはブラウザのみ使用する必要があります\n\n本当に有効化しますか？';

    if (!confirm(confirmMessage)) {
      return;
    }

    setQuicUpdating(true);
    try {
      const result = await adminAPI.updateQuicBlocking(!quicBlocking.enabled);
      setQuicBlocking({ enabled: result.enabled });
      alert(`QUICブロッキングを${action}しました\n\n${result.message}`);
    } catch (error: any) {
      alert(`QUICブロッキングの${action}に失敗しました:\n${error.message}`);
    } finally {
      setQuicUpdating(false);
    }
  }

  function handleDownloadCSV() {
    const headers = ['Code', 'Status', 'Name', 'Email', 'Language', 'Device GUID', 'Created At', 'Activated At', 'Notes'];
    const csvData = codes.map(code => [
      code.code,
      code.status,
      code.memberName || '',
      code.memberEmail || '',
      code.memberLanguage || '',
      code.deviceGuid || '',
      new Date(code.createdAt).toLocaleString('ja-JP'),
      code.activatedAt ? new Date(code.activatedAt).toLocaleString('ja-JP') : '',
      code.notes || '',
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `member-codes-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  }

  async function handleDownloadLifecycleCSV() {
    // Hits the backend export endpoint so SCR gets the same per-code source-of-truth
    // (vpn handshakes, ad event counts) used for the KPI cards.
    const res = await fetch('/api/admin/export/members-lifecycle', { credentials: 'include' });
    if (!res.ok) {
      alert('CSV download failed');
      return;
    }
    const blob = await res.blob();
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `members_lifecycle_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  }

  async function handleDownloadDailyCSV() {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dailyDate)) {
      alert('Invalid date');
      return;
    }
    setDownloadingDailyCsv(true);
    try {
      const res = await fetch(`/api/admin/export/daily-activity?date=${dailyDate}`, { credentials: 'include' });
      if (!res.ok) {
        alert('Daily CSV download failed');
        return;
      }
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `daily_activity_${dailyDate.replace(/-/g, '')}.csv`;
      link.click();
    } finally {
      setDownloadingDailyCsv(false);
    }
  }

  async function handleShowLoginHistory(code: string) {
    setLoginHistoryCode(code);
    setLoadingHistory(true);
    try {
      const history = await adminAPI.getMemberLoginHistory(code, 50);
      setLoginHistory(history);
    } catch (error) {
      console.error('Failed to load login history:', error);
      setLoginHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  }

  function formatLoginLocation(location: GeoLocation | null): string {
    if (!location) return '-';
    const parts: string[] = [];
    if (location.city) parts.push(location.city);
    if (location.country) parts.push(location.country);
    return parts.length > 0 ? parts.join(', ') : '-';
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-kido-50 dark:bg-[#1a1612] flex items-center justify-center">
        <div className="text-xl text-gray-600 dark:text-gray-400">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-kido-50 dark:bg-[#1a1612]">
      <div className="max-w-7xl mx-auto px-4 py-4">
        {/* Page header (sidebar handles global nav, theme, language, logout) */}
        <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('memberManagement.title')}</h1>
            {isReadOnly && (
              <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                <Lock className="w-3 h-3" />
                {t('memberManagement.readOnlyBadge')}
              </span>
            )}
            <p className="text-xs text-gray-500 dark:text-gray-400">{getVersionString()}</p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 px-3 py-1.5 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white rounded-lg text-sm font-semibold transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            {t('common.refresh')}
          </button>
        </div>
        {/* QUIC Blocking Control — sasadmin only (system-level toggle) */}
        {isSasadmin && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-warm-gray-800 dark:to-warm-gray-800 border border-blue-200 dark:border-warm-gray-700 rounded-xl shadow-subtle p-3 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Shield className={`w-10 h-10 ${quicBlocking.enabled ? 'text-green-600' : 'text-gray-400'}`} />
              <div>
                <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100">{t('dashboard.quicBlocking')}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {quicBlocking.enabled
                    ? '✅ 有効: 全デバイスはHTTPS/TCPを使用 (mitmproxyでキャプチャ可能、ネイティブアプリは動作しません)'
                    : '❌ 無効: デバイスはQUICを使用 (ネイティブアプリは動作、広告キャプチャは不可)'}
                </p>
              </div>
            </div>
            <button
              onClick={handleToggleQuicBlocking}
              disabled={quicUpdating}
              className={`px-6 py-3 rounded-lg font-bold text-white transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed ${
                quicBlocking.enabled
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-green-600 hover:bg-green-700'
              }`}
            >
              {quicUpdating ? t('dashboard.processing') : quicBlocking.enabled ? t('dashboard.disable') : t('dashboard.enable')}
            </button>
          </div>
        </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
          <div className="bg-white dark:bg-warm-gray-800 rounded-xl border border-warm-gray-200 dark:border-warm-gray-700 shadow-subtle p-3">
            <div className="flex items-center gap-2 mb-1">
              <Users className="w-5 h-5 text-blue-500" />
              <span className="text-sm text-gray-600 dark:text-gray-400">{t('dashboard.totalCodes')}</span>
            </div>
            <p className="text-2xl font-bold text-gray-800 dark:text-gray-100">{(stats?.totalCodes || 0).toLocaleString()}</p>
          </div>

          <div className="bg-white dark:bg-warm-gray-800 rounded-xl border border-warm-gray-200 dark:border-warm-gray-700 shadow-subtle p-3">
            <div className="flex items-center gap-2 mb-1">
              <Activity className="w-5 h-5 text-green-500" />
              <span className="text-sm text-gray-600 dark:text-gray-400">{t('dashboard.activeCodes')}</span>
            </div>
            <p className="text-2xl font-bold text-gray-800 dark:text-gray-100">{(stats?.activeCodes || 0).toLocaleString()}</p>
          </div>

          <div className="bg-white dark:bg-warm-gray-800 rounded-xl border border-warm-gray-200 dark:border-warm-gray-700 shadow-subtle p-3">
            <div className="flex items-center gap-2 mb-1">
              <Wifi className="w-5 h-5 text-cyan-600" />
              <span className="text-sm text-gray-600 dark:text-gray-400">{t('dashboard.connectedDevices')}</span>
            </div>
            <p className="text-2xl font-bold text-gray-800 dark:text-gray-100">{(stats?.totalDevices || 0).toLocaleString()}</p>
          </div>

          <div className="bg-white dark:bg-warm-gray-800 rounded-xl border border-warm-gray-200 dark:border-warm-gray-700 shadow-subtle p-3">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-5 h-5 text-orange-500" />
              <span className="text-sm text-gray-600 dark:text-gray-400">{t('dashboard.adsToday')}</span>
            </div>
            <p className="text-2xl font-bold text-gray-800 dark:text-gray-100">{(stats?.adsToday || 0).toLocaleString()}</p>
          </div>

          <div className="bg-white dark:bg-warm-gray-800 rounded-xl border border-warm-gray-200 dark:border-warm-gray-700 shadow-subtle p-3">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-5 h-5 text-red-500" />
              <span className="text-sm text-gray-600 dark:text-gray-400">{t('dashboard.totalAds')}</span>
            </div>
            <p className="text-2xl font-bold text-gray-800 dark:text-gray-100">{(stats?.adsTotal || 0).toLocaleString()}</p>
          </div>
        </div>

        {/* Generate Code — sasadmin only (PII: member name/email) */}
        {isSasadmin && (
        <div className="bg-white dark:bg-warm-gray-800 rounded-xl border border-warm-gray-200 dark:border-warm-gray-700 shadow-subtle p-3 mb-4">
          <h2 className="text-sm font-bold text-gray-800 dark:text-gray-100 mb-2">{t('dashboard.generateNewCode')}</h2>
          <div className="flex gap-2 flex-wrap items-end">
            <div className="flex-1 min-w-[120px]">
              <label className="text-xs text-gray-500 dark:text-gray-400 mb-0.5 block">{t('dashboard.memberName')}</label>
              <input
                type="text"
                value={memberName}
                onChange={(e) => setMemberName(e.target.value)}
                placeholder="例: 山田太郎"
                className="w-full px-2 py-1.5 border border-warm-gray-300 rounded focus:border-kido-500 focus:ring-1 focus:ring-kido-300 transition-all outline-none text-sm dark:bg-warm-gray-700 dark:border-warm-gray-600 dark:text-white"
                disabled={generating}
              />
            </div>
            <div className="flex-1 min-w-[140px]">
              <label className="text-xs text-gray-500 dark:text-gray-400 mb-0.5 block">{t('dashboard.memberEmail')}</label>
              <input
                type="email"
                value={memberEmail}
                onChange={(e) => setMemberEmail(e.target.value)}
                placeholder="例: user@example.com"
                className="w-full px-2 py-1.5 border border-warm-gray-300 rounded focus:border-kido-500 focus:ring-1 focus:ring-kido-300 transition-all outline-none text-sm dark:bg-warm-gray-700 dark:border-warm-gray-600 dark:text-white"
                disabled={generating}
              />
            </div>
            <div className="flex-1 min-w-[100px]">
              <label className="text-xs text-gray-500 dark:text-gray-400 mb-0.5 block">{t('dashboard.notes')}</label>
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="任意"
                className="w-full px-2 py-1.5 border border-warm-gray-300 rounded focus:border-kido-500 focus:ring-1 focus:ring-kido-300 transition-all outline-none text-sm dark:bg-warm-gray-700 dark:border-warm-gray-600 dark:text-white"
                disabled={generating}
              />
            </div>
            <button
              onClick={handleGenerateCode}
              disabled={generating}
              className="flex items-center gap-1 px-4 py-1.5 bg-kido-600 hover:bg-kido-700 disabled:bg-gray-400 text-white rounded font-semibold transition-colors text-sm"
            >
              <Plus className="w-4 h-4" />
              <span>{generating ? t('common.generating') : t('common.generate')}</span>
            </button>
            <button
              onClick={() => setBulkOpen(true)}
              disabled={generating}
              className="flex items-center gap-1 px-4 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded font-semibold transition-colors text-sm"
              title={t('bulkCodes.title')}
            >
              <Upload className="w-4 h-4" />
              <span>{t('bulkCodes.button')}</span>
            </button>
          </div>
        </div>
        )}
        {bulkOpen && <BulkGenerateModal onClose={() => setBulkOpen(false)} onDone={() => { setBulkOpen(false); void loadDashboard(); }} />}

        {/* Codes List — visible to both roles. Sys-operator sees PII-masked rows
            (server redacts) and no edit/revoke/delete/CSV/template buttons. */}
        <div className="bg-white dark:bg-warm-gray-800 rounded-xl border border-warm-gray-200 dark:border-warm-gray-700 shadow-subtle p-3">
          {/* Lifecycle KPI Cards (No.1: SCR registration tracking) */}
          {lifecycle && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
              <div className="bg-gray-50 dark:bg-warm-gray-700/50 rounded-lg p-3 border border-gray-200 dark:border-warm-gray-600">
                <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">{t('dashboard.lifecycleIssued', 'コード発行済み')}</div>
                <div className="text-2xl font-bold text-gray-700 dark:text-gray-200">{lifecycle.issued.toLocaleString()}</div>
              </div>
              <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-3 border border-blue-200 dark:border-blue-700">
                <div className="text-xs font-semibold text-blue-600 dark:text-blue-300 mb-1">{t('dashboard.lifecycleRegistered', '登録完了')}</div>
                <div className="text-2xl font-bold text-blue-700 dark:text-blue-200">{lifecycle.registered.toLocaleString()}</div>
              </div>
              <div className="bg-green-50 dark:bg-green-900/30 rounded-lg p-3 border border-green-200 dark:border-green-700">
                <div className="text-xs font-semibold text-green-600 dark:text-green-300 mb-1">{t('dashboard.lifecycleStartedUsing', '利用開始')}</div>
                <div className="text-2xl font-bold text-green-700 dark:text-green-200">{lifecycle.startedUsing.toLocaleString()}</div>
              </div>
              <div className="bg-emerald-50 dark:bg-emerald-900/30 rounded-lg p-3 border border-emerald-200 dark:border-emerald-700">
                <div className="text-xs font-semibold text-emerald-600 dark:text-emerald-300 mb-1">{t('dashboard.lifecycleActive', 'アクティブ')}</div>
                <div className="text-2xl font-bold text-emerald-700 dark:text-emerald-200">{lifecycle.active.toLocaleString()}</div>
              </div>
            </div>
          )}
          <div className="flex justify-between items-center mb-2 flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <h2 className="text-sm font-bold text-gray-800 dark:text-gray-100">{t('dashboard.codeList')}</h2>
              <input
                type="text"
                value={codesSearch}
                onChange={(e) => { setCodesSearch(e.target.value); setCodesPage(0); }}
                placeholder={t('dashboard.searchPlaceholder')}
                className="px-2 py-1 border border-warm-gray-300 rounded text-sm w-56 focus:border-kido-500 focus:ring-1 focus:ring-kido-300 outline-none dark:bg-warm-gray-700 dark:border-warm-gray-600 dark:text-white"
              />
              <span className="text-xs text-gray-500">
                {(() => {
                  const filtered = codes.filter(c => {
                    if (!codesSearch) return true;
                    const q = codesSearch.toLowerCase();
                    return (c.code?.toLowerCase().includes(q) || c.memberName?.toLowerCase().includes(q) || c.memberEmail?.toLowerCase().includes(q) || c.notes?.toLowerCase().includes(q));
                  });
                  return `${filtered.length} / ${codes.length} 件`;
                })()}
              </span>
            </div>
            {isSasadmin && (
            <div className="flex gap-2">
              <button
                onClick={handleDownloadCSV}
                className="flex items-center gap-1 px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded font-semibold transition-colors text-sm"
              >
                <Download className="w-4 h-4" />
                <span>CSV</span>
              </button>
              <button
                onClick={handleDownloadLifecycleCSV}
                className="flex items-center gap-1 px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded font-semibold transition-colors text-sm"
                title={t('dashboard.lifecycleCsvHint', 'ライフサイクルCSV — SCR提出用') as string}
              >
                <Download className="w-4 h-4" />
                <span>{t('dashboard.lifecycleCsvButton', 'ライフサイクルCSV')}</span>
              </button>
              <div
                className="flex items-center gap-1 pl-2 border-l border-warm-gray-300 dark:border-warm-gray-600"
                title={t('dashboard.dailyCsvHint', '指定日の利用状況（本日と累計）を1ユーザー1行で出力 — 顧客提出用') as string}
              >
                <input
                  type="date"
                  value={dailyDate}
                  onChange={(e) => setDailyDate(e.target.value)}
                  aria-label={t('dashboard.dailyCsvDateLabel', '対象日') as string}
                  className="px-2 py-1 border border-warm-gray-300 rounded text-sm focus:border-kido-500 focus:ring-1 focus:ring-kido-300 outline-none dark:bg-warm-gray-700 dark:border-warm-gray-600 dark:text-white"
                />
                <button
                  onClick={handleDownloadDailyCSV}
                  disabled={downloadingDailyCsv}
                  className="flex items-center gap-1 px-3 py-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white rounded font-semibold transition-colors text-sm"
                >
                  <Download className="w-4 h-4" />
                  <span>{t('dashboard.dailyCsvButton', '日次活動CSV')}</span>
                </button>
              </div>
              <button
                onClick={handleTemplateSettingsClick}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold transition-colors"
              >
                <FileText className="w-4 h-4" />
                <span>テンプレート設定</span>
              </button>
            </div>
            )}
          </div>
          <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
            <table className="w-full">
              <thead className="sticky top-0 bg-kido-50 dark:bg-warm-gray-700 z-10">
                <tr className="border-b border-warm-gray-200">
                  <th className="text-left py-1.5 px-3 font-semibold text-gray-700 dark:text-gray-300 text-sm w-36">{t('dashboard.code')}</th>
                  <th className="text-left py-1.5 px-3 font-semibold text-gray-700 dark:text-gray-300 text-sm w-20">{t('common.status')}</th>
                  <th className="text-left py-1.5 px-3 font-semibold text-gray-700 dark:text-gray-300 text-sm w-28">{t('dashboard.lifecycleColumn', 'ライフサイクル')}</th>
                  <th className="text-left py-1.5 px-3 font-semibold text-gray-700 dark:text-gray-300 text-sm w-40">{t('dashboard.memberName')}</th>
                  <th className="text-left py-1.5 px-3 font-semibold text-gray-700 dark:text-gray-300 text-sm">{t('dashboard.memberEmail')}</th>
                  <th className="text-left py-1.5 px-3 font-semibold text-gray-700 dark:text-gray-300 text-sm w-16">{t('dashboard.language')}</th>
                  <th className="text-left py-1.5 px-3 font-semibold text-gray-700 dark:text-gray-300 text-sm w-24">{t('dashboard.createdAt')}</th>
                  <th className="text-left py-1.5 px-3 font-semibold text-gray-700 dark:text-gray-300 text-sm w-28">{t('dashboard.lastLogin')}</th>
                  <th className="text-left py-1.5 px-3 font-semibold text-gray-700 dark:text-gray-300 text-sm w-44">{t('dashboard.notes')}</th>
                  {isSasadmin && (
                    <th className="text-left py-1.5 px-3 font-semibold text-gray-700 dark:text-gray-300 text-sm w-48">{t('common.actions')}</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {codes.filter(c => {
                  if (!codesSearch) return true;
                  const q = codesSearch.toLowerCase();
                  return (c.code?.toLowerCase().includes(q) || c.memberName?.toLowerCase().includes(q) || c.memberEmail?.toLowerCase().includes(q) || c.notes?.toLowerCase().includes(q));
                }).slice(codesPage * 20, (codesPage + 1) * 20).map((code, index) => (
                  <tr key={code.id} className={`border-b border-gray-100 dark:border-warm-gray-700 hover:bg-kido-50 dark:hover:bg-warm-gray-700 ${index % 2 === 0 ? 'bg-white dark:bg-warm-gray-800' : 'bg-gray-50 dark:bg-warm-gray-900'}`}>
                    <td className="py-3 px-4 font-mono font-semibold text-gray-800 dark:text-gray-200">{code.code}</td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                        code.status === 'active' ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300' :
                        code.status === 'revoked' ? 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300' :
                        'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                      }`}>
                        {code.status}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      {(() => {
                        const stage = code.lifecycleStage || 'issued';
                        const stageStyle: Record<string, string> = {
                          active:        'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300',
                          started_using: 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300',
                          registered:    'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300',
                          issued:        'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
                        };
                        return (
                          <span
                            className={`px-2 py-1 rounded text-xs font-semibold ${stageStyle[stage]}`}
                            title={
                              stage === 'active' ? `${code.adEventCount ?? 0} ads` :
                              stage === 'started_using' && code.vpnLastSeen ? new Date(code.vpnLastSeen).toLocaleString('ja-JP') :
                              stage === 'registered' && code.activatedAt ? new Date(code.activatedAt).toLocaleString('ja-JP') :
                              ''
                            }
                          >
                            {t(`dashboard.lifecycleStage_${stage}`, {
                              active: 'アクティブ',
                              started_using: '利用開始',
                              registered: '登録完了',
                              issued: '発行済み',
                            }[stage]) as string}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                      {code.memberName || '-'}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                      {code.memberEmail || '-'}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                      {code.memberLanguage === 'ja' ? '日本語' : code.memberLanguage === 'vi' ? 'Tiếng Việt' : code.memberLanguage || '-'}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                      {new Date(code.createdAt).toLocaleDateString('ja-JP')}
                    </td>
                    <td className="py-3 px-4 text-sm">
                      {code.lastLoginAt ? (
                        isSasadmin ? (
                          <button
                            onClick={() => handleShowLoginHistory(code.code)}
                            className="flex flex-col items-start hover:bg-blue-50 dark:hover:bg-blue-900/30 p-1 rounded transition-colors cursor-pointer"
                            title="クリックして履歴を表示"
                          >
                            <span className="flex items-center gap-1 text-blue-600">
                              <MapPin className="w-3 h-3" />
                              {formatLoginLocation(code.lastLoginLocation as GeoLocation | null)}
                            </span>
                            <span className="flex items-center gap-1 text-gray-500 text-xs">
                              <Clock className="w-3 h-3" />
                              {new Date(code.lastLoginAt).toLocaleString('ja-JP')}
                            </span>
                          </button>
                        ) : (
                          <div className="flex flex-col items-start p-1">
                            <span className="flex items-center gap-1 text-gray-500 text-xs">
                              <Clock className="w-3 h-3" />
                              {new Date(code.lastLoginAt).toLocaleString('ja-JP')}
                            </span>
                          </div>
                        )
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                      {code.notes || '-'}
                    </td>
                    {isSasadmin && (
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2 flex-wrap">
                        <button
                          onClick={() => handleEditClick(code)}
                          className="flex items-center gap-1 px-3 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 dark:bg-blue-900/50 dark:hover:bg-blue-900/70 dark:text-blue-300 rounded transition-colors"
                        >
                          <Edit className="w-4 h-4" />
                          <span className="text-xs font-semibold">{t('dashboard.editButton')}</span>
                        </button>
                        {code.memberName && code.memberEmail && (
                          <button
                            onClick={() => handleCopyEmail(code)}
                            className={`flex items-center gap-1 px-3 py-1 rounded transition-colors ${
                              copySuccess === code.code
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300'
                                : 'bg-purple-100 hover:bg-purple-200 text-purple-700 dark:bg-purple-900/50 dark:hover:bg-purple-900/70 dark:text-purple-300'
                            }`}
                          >
                            {copySuccess === code.code ? (
                              <>
                                <Mail className="w-4 h-4" />
                                <span className="text-xs font-semibold">{t('dashboard.mailCopied')}</span>
                              </>
                            ) : (
                              <>
                                <Copy className="w-4 h-4" />
                                <span className="text-xs font-semibold">{t('dashboard.mailButton')}</span>
                              </>
                            )}
                          </button>
                        )}
                        {code.status === 'active' && (
                          <button
                            onClick={() => handleRevokeCode(code.code)}
                            className="flex items-center gap-1 px-3 py-1 bg-yellow-100 hover:bg-yellow-200 text-yellow-700 dark:bg-yellow-900/50 dark:hover:bg-yellow-900/70 dark:text-yellow-300 rounded transition-colors"
                          >
                            <XCircle className="w-4 h-4" />
                            <span className="text-xs font-semibold">{t('dashboard.revokeButton')}</span>
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteCode(code.code)}
                          className="flex items-center gap-1 px-3 py-1 bg-red-100 hover:bg-red-200 text-red-700 dark:bg-red-900/50 dark:hover:bg-red-900/70 dark:text-red-300 rounded transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                          <span className="text-xs font-semibold">{t('dashboard.deleteButton')}</span>
                        </button>
                      </div>
                    </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          {(() => {
            const filtered = codes.filter(c => {
              if (!codesSearch) return true;
              const q = codesSearch.toLowerCase();
              return (c.code?.toLowerCase().includes(q) || c.memberName?.toLowerCase().includes(q) || c.memberEmail?.toLowerCase().includes(q) || c.notes?.toLowerCase().includes(q));
            });
            const totalPages = Math.ceil(filtered.length / 20);
            if (totalPages <= 1) return null;
            return (
              <div className="flex items-center justify-between px-3 py-2 border-t border-warm-gray-200 text-sm">
                <span className="text-gray-500">{codesPage * 20 + 1} - {Math.min((codesPage + 1) * 20, filtered.length)} / {filtered.length} 件</span>
                <div className="flex gap-1">
                  <button onClick={() => setCodesPage(Math.max(0, codesPage - 1))} disabled={codesPage === 0} className="px-2 py-1 border border-warm-gray-300 rounded text-sm disabled:opacity-50">前へ</button>
                  <button onClick={() => setCodesPage(Math.min(totalPages - 1, codesPage + 1))} disabled={codesPage >= totalPages - 1} className="px-2 py-1 border border-warm-gray-300 rounded text-sm disabled:opacity-50">次へ</button>
                </div>
              </div>
            );
          })()}
        </div>

        {editingCode && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-warm-gray-800 rounded-xl shadow-lg p-6 max-w-2xl w-full mx-4">
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-6">{t('dashboard.editUser')}</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">コード</label>
                  <input
                    type="text"
                    value={editingCode.code}
                    disabled
                    className="w-full px-4 py-2 border-2 border-gray-300 dark:border-warm-gray-600 rounded-lg bg-gray-100 dark:bg-warm-gray-700 dark:text-gray-100 font-mono"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">名前 *</label>
                  <input
                    type="text"
                    value={editForm.memberName}
                    onChange={(e) => setEditForm({ ...editForm, memberName: e.target.value })}
                    placeholder="Member Name"
                    className="w-full px-4 py-2 border-2 border-gray-300 dark:border-warm-gray-600 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-300 transition-all outline-none dark:bg-warm-gray-700 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">{t('dashboard.emailAddressLabel')}</label>
                  <input
                    type="email"
                    value={editForm.memberEmail}
                    onChange={(e) => setEditForm({ ...editForm, memberEmail: e.target.value })}
                    placeholder="member@example.com"
                    className="w-full px-4 py-2 border-2 border-gray-300 dark:border-warm-gray-600 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-300 transition-all outline-none dark:bg-warm-gray-700 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">言語</label>
                  <select
                    value={editForm.memberLanguage}
                    onChange={(e) => setEditForm({ ...editForm, memberLanguage: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-300 dark:border-warm-gray-600 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-300 transition-all outline-none dark:bg-warm-gray-700 dark:text-gray-100"
                  >
                    <option value="ja">日本語</option>
                    <option value="vi">Tiếng Việt</option>
                    <option value="en">English</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">メモ</label>
                  <textarea
                    value={editForm.notes}
                    onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                    placeholder="Optional notes"
                    rows={3}
                    className="w-full px-4 py-2 border-2 border-gray-300 dark:border-warm-gray-600 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-300 transition-all outline-none dark:bg-warm-gray-700 dark:text-gray-100"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-4 mt-6">
                <button
                  onClick={() => setEditingCode(null)}
                  disabled={updating}
                  className="px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 dark:bg-warm-gray-600 dark:hover:bg-warm-gray-500 dark:text-gray-100 rounded-lg font-semibold transition-colors"
                >
                  キャンセル
                </button>
                <button
                  onClick={handleUpdateCode}
                  disabled={updating}
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg font-semibold transition-colors"
                >
                  {updating ? '更新中...' : '更新'}
                </button>
              </div>
            </div>
          </div>
        )}

        {showTemplateSettings && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-warm-gray-800 rounded-xl shadow-lg p-6 max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto relative">
              {/* Close X button at top-right */}
              <button
                onClick={() => setShowTemplateSettings(false)}
                className="absolute top-4 right-4 p-1 text-gray-400 hover:text-gray-600 transition-colors"
                title="Close"
              >
                <X className="w-6 h-6" />
              </button>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-6">{t('dashboard.templateSettings')}</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">{t('dashboard.emailTitleLabel')}</label>
                  <input
                    type="text"
                    value={templateForm.emailTitle}
                    onChange={(e) => setTemplateForm({ ...templateForm, emailTitle: e.target.value })}
                    placeholder="SAS member activation letter"
                    className="w-full px-4 py-2 border-2 border-gray-300 dark:border-warm-gray-600 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-300 transition-all outline-none dark:bg-warm-gray-700 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Android APK URL (Google Drive)</label>
                  <input
                    type="text"
                    value={templateForm.androidApkUrl}
                    onChange={(e) => setTemplateForm({ ...templateForm, androidApkUrl: e.target.value })}
                    placeholder="https://drive.google.com/file/d/..."
                    className="w-full px-4 py-2 border-2 border-gray-300 dark:border-warm-gray-600 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-300 transition-all outline-none dark:bg-warm-gray-700 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Android APK URL (Diawi)</label>
                  <input
                    type="text"
                    value={templateForm.androidDiawiUrl}
                    onChange={(e) => setTemplateForm({ ...templateForm, androidDiawiUrl: e.target.value })}
                    placeholder="https://i.diawi.com/..."
                    className="w-full px-4 py-2 border-2 border-gray-300 dark:border-warm-gray-600 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-300 transition-all outline-none dark:bg-warm-gray-700 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">iOS App Store URL</label>
                  <input
                    type="text"
                    value={templateForm.iosAppstoreUrl}
                    onChange={(e) => setTemplateForm({ ...templateForm, iosAppstoreUrl: e.target.value })}
                    placeholder="https://apps.apple.com/jp/app/..."
                    className="w-full px-4 py-2 border-2 border-gray-300 dark:border-warm-gray-600 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-300 transition-all outline-none dark:bg-warm-gray-700 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Android Play Store URL (Optional)</label>
                  <input
                    type="text"
                    value={templateForm.androidPlaystoreUrl || ''}
                    onChange={(e) => setTemplateForm({ ...templateForm, androidPlaystoreUrl: e.target.value })}
                    placeholder="https://play.google.com/store/apps/..."
                    className="w-full px-4 py-2 border-2 border-gray-300 dark:border-warm-gray-600 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-300 transition-all outline-none dark:bg-warm-gray-700 dark:text-gray-100"
                  />
                </div>

                {/* APK Upload & History Section */}
                <div className="border-t-2 border-gray-200 pt-4 mt-4">
                  <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-3 flex items-center gap-2">
                    <Smartphone className="w-5 h-5 text-green-600" />
                    APK Direct Upload (Server Hosting)
                  </h3>

                  {/* Upload form */}
                  <div className="space-y-3 mb-4">
                    <div className="flex gap-3">
                      <div className="flex-1">
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Version (same version = new build, all kept)</label>
                        <input
                          type="text"
                          value={apkVersion}
                          onChange={(e) => setApkVersion(e.target.value)}
                          placeholder={apkList.length > 0 ? apkList[0].version : 'v1.0.1'}
                          className="w-full px-3 py-2 border-2 border-gray-300 dark:border-warm-gray-600 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-300 transition-all outline-none text-sm dark:bg-warm-gray-700 dark:text-gray-100"
                        />
                      </div>
                      <div className="flex-1">
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Notes (optional)</label>
                        <input
                          type="text"
                          value={apkNotes}
                          onChange={(e) => setApkNotes(e.target.value)}
                          placeholder="Build notes..."
                          className="w-full px-3 py-2 border-2 border-gray-300 dark:border-warm-gray-600 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-300 transition-all outline-none text-sm dark:bg-warm-gray-700 dark:text-gray-100"
                        />
                      </div>
                    </div>
                    <label className={`flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                      apkUploading
                        ? 'border-gray-300 bg-gray-50 cursor-wait'
                        : 'border-green-400 hover:border-green-500 hover:bg-green-50'
                    }`}>
                      <Upload className="w-5 h-5 text-green-600" />
                      <span className="text-sm font-semibold text-green-700">
                        {apkUploading ? 'Uploading...' : 'Click to upload APK file'}
                      </span>
                      <input
                        type="file"
                        accept=".apk"
                        onChange={handleApkUpload}
                        disabled={apkUploading}
                        className="hidden"
                      />
                    </label>
                  </div>

                  {/* APK History List (accordion, max 5, latest on top) */}
                  {apkList.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">APK History (latest {apkList.length})</p>
                      {apkList.map((apk, index) => {
                        const isExpanded = expandedApkId === apk.id;
                        const isLatest = index === 0;
                        return (
                          <div
                            key={apk.id}
                            className={`border rounded-lg overflow-hidden transition-all ${
                              isLatest ? 'border-green-300' : 'border-gray-200'
                            }`}
                          >
                            {/* Collapsed header — click to expand */}
                            <button
                              onClick={() => setExpandedApkId(isExpanded ? null : apk.id)}
                              className={`w-full flex items-center justify-between px-4 py-3 text-left transition-colors ${
                                isLatest
                                  ? 'bg-green-50 hover:bg-green-100'
                                  : 'bg-gray-50 hover:bg-kido-50'
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                {isLatest && (
                                  <span className="px-2 py-0.5 bg-green-600 text-white text-xs font-bold rounded">LATEST</span>
                                )}
                                <span className="font-semibold text-sm text-gray-800 dark:text-gray-200">{apk.version}</span>
                                <span className="text-xs text-gray-500">
                                  {(apk.fileSize / 1024 / 1024).toFixed(1)} MB
                                </span>
                                <span className="text-xs text-gray-400">
                                  {new Date(apk.uploadedAt).toLocaleString('ja-JP')}
                                </span>
                                {apk.notes && (
                                  <span className="text-xs text-gray-500 italic truncate max-w-[200px]">{apk.notes}</span>
                                )}
                              </div>
                              <span className={`text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                                ▼
                              </span>
                            </button>

                            {/* Expanded content */}
                            {isExpanded && (
                              <div className="px-4 py-4 bg-white dark:bg-warm-gray-800 border-t border-gray-100 dark:border-warm-gray-700">
                                <div className="flex gap-4">
                                  <div className="flex-1 space-y-1">
                                    <p className="text-sm text-gray-700"><span className="font-semibold">Version:</span> {apk.version}</p>
                                    <p className="text-sm text-gray-700"><span className="font-semibold">File:</span> {apk.originalName}</p>
                                    <p className="text-sm text-gray-700"><span className="font-semibold">Size:</span> {(apk.fileSize / 1024 / 1024).toFixed(1)} MB</p>
                                    <p className="text-sm text-gray-700"><span className="font-semibold">Uploaded:</span> {new Date(apk.uploadedAt).toLocaleString('ja-JP')}</p>
                                    {apk.notes && (
                                      <p className="text-sm text-gray-700"><span className="font-semibold">Notes:</span> {apk.notes}</p>
                                    )}
                                    <a
                                      href={apk.downloadUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-sm text-blue-600 underline block mt-2"
                                    >
                                      {apk.downloadUrl}
                                    </a>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleDeleteApk(apk.id, apk.version); }}
                                      className="mt-3 flex items-center gap-1 px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 text-xs font-semibold rounded-lg border border-red-200 transition-colors"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                      Delete this APK
                                    </button>
                                  </div>
                                  {apk.qrCode && (
                                    <div className="flex flex-col items-center flex-shrink-0">
                                      <p className="text-xs text-gray-500 mb-1">Download QR</p>
                                      <img src={apk.qrCode} alt="APK Download QR Code" className="w-32 h-32" />
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {apkList.length === 0 && (
                    <p className="text-sm text-gray-400 italic">No APK uploaded yet</p>
                  )}
                </div>
              </div>
              <div className="flex justify-end gap-4 mt-6">
                <button
                  onClick={() => setShowTemplateSettings(false)}
                  disabled={updating}
                  className="px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 dark:bg-warm-gray-600 dark:hover:bg-warm-gray-500 dark:text-gray-100 rounded-lg font-semibold transition-colors"
                >
                  キャンセル
                </button>
                <button
                  onClick={handleUpdateTemplate}
                  disabled={updating}
                  className="px-6 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white rounded-lg font-semibold transition-colors"
                >
                  {updating ? '更新中...' : '更新'}
                </button>
              </div>
            </div>
          </div>
        )}

        {loginHistoryCode && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-warm-gray-800 rounded-xl shadow-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2">
                  <History className="w-6 h-6 text-blue-600" />
                  {t('dashboard.loginHistory')}: {loginHistoryCode}
                </h2>
                <button
                  onClick={() => setLoginHistoryCode(null)}
                  className="p-2 hover:bg-kido-50 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6 text-gray-500" />
                </button>
              </div>

              {loadingHistory ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-gray-600">{t('common.loading')}</div>
                </div>
              ) : loginHistory.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-gray-500">ログイン履歴がありません</div>
                </div>
              ) : (
                <div className="overflow-y-auto flex-1">
                  <table className="w-full">
                    <thead className="sticky top-0 bg-white dark:bg-warm-gray-700">
                      <tr className="border-b border-warm-gray-200 dark:border-warm-gray-600">
                        <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">{t('dashboard.datetime')}</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">{t('dashboard.ipAddress')}</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">{t('dashboard.location')}</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">{t('dashboard.device')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loginHistory.map((entry, index) => (
                        <tr key={entry.id} className={`border-b border-gray-100 dark:border-warm-gray-700 ${index % 2 === 0 ? 'bg-white dark:bg-warm-gray-800' : 'bg-gray-50 dark:bg-warm-gray-900'}`}>
                          <td className="py-3 px-4 text-sm">
                            {new Date(entry.loginAt).toLocaleString('ja-JP')}
                          </td>
                          <td className="py-3 px-4 text-sm font-mono">
                            {entry.loginIp || '-'}
                          </td>
                          <td className="py-3 px-4 text-sm">
                            {entry.loginLocation ? (
                              <div className="flex flex-col">
                                <span className="flex items-center gap-1">
                                  <MapPin className="w-3 h-3 text-blue-500" />
                                  {formatLoginLocation(entry.loginLocation)}
                                </span>
                                {entry.loginLocation.isp && (
                                  <span className="text-xs text-gray-500">{entry.loginLocation.isp}</span>
                                )}
                              </div>
                            ) : (
                              '-'
                            )}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600 max-w-xs truncate" title={entry.userAgent || '-'}>
                            {entry.userAgent ? (
                              entry.userAgent.includes('iPhone') ? 'iPhone' :
                              entry.userAgent.includes('Android') ? 'Android' :
                              entry.userAgent.includes('Windows') ? 'Windows' :
                              entry.userAgent.includes('Mac') ? 'Mac' :
                              entry.userAgent.includes('Linux') ? 'Linux' :
                              'Unknown'
                            ) : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="flex justify-end mt-6 pt-4 border-t">
                <button
                  onClick={() => setLoginHistoryCode(null)}
                  className="px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 dark:bg-warm-gray-600 dark:hover:bg-warm-gray-500 dark:text-gray-100 rounded-lg font-semibold transition-colors"
                >
                  閉じる
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Mirror the backend `applyTemplate` so the live preview shows what the user
// will actually get. Empty/whitespace template -> null (no preview).
function previewTemplate(template: string, index: number): string {
  const t = template.trim();
  if (!t) return '';
  return t.replace(/X+/g, (run) => String(index).padStart(run.length, '0'));
}

function BulkGenerateModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const [fromIdx, setFromIdx] = useState(1);
  const [toIdx, setToIdx] = useState(50);
  const [nameTemplate, setNameTemplate] = useState('SAS-Member-XXX');
  const [emailTemplate, setEmailTemplate] = useState('sas-member-XXX@gmail.com');
  const [notesTemplate, setNotesTemplate] = useState('SAS-Member-XXX');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<string | null>(null);

  const count = Math.max(0, toIdx - fromIdx + 1);
  const estSeconds = Math.max(1, Math.round(count * 1.2));
  const rangeOk = Number.isFinite(fromIdx) && Number.isFinite(toIdx)
    && fromIdx >= 1 && toIdx >= fromIdx && count >= 1 && count <= 50;
  const previewFirst = previewTemplate(nameTemplate, fromIdx);
  const previewLast = previewTemplate(nameTemplate, toIdx);

  async function submit() {
    if (!rangeOk) { setError(t('bulkCodes.rangeInvalid')); return; }
    setSubmitting(true); setError(null);
    setProgress(t('bulkCodes.estimate', { seconds: estSeconds }));
    try {
      const res = await adminAPI.bulkGenerateCodes({
        fromIndex: fromIdx,
        toIndex: toIdx,
        nameTemplate,
        emailTemplate,
        notesTemplate,
      });
      // Download CSV blob whether full success or partial
      const ts = new Date().toISOString().replace(/[-:]/g, '').slice(0, 13).replace('T', '_');
      const blob = new Blob([res.csv ?? ''], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bulk_codes_${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      if (res.success) {
        onDone();
      } else {
        setError(t('bulkCodes.partialFailure', { completed: res.completed ?? 0, failedAt: res.failedAt ?? 0, error: res.error ?? '' }));
      }
    } catch (e: any) {
      setError(e?.message ?? 'Failed');
    } finally {
      setSubmitting(false);
      setProgress(null);
    }
  }

  const inputCls = 'w-full px-3 py-2 rounded border border-warm-gray-300 dark:border-warm-gray-600 bg-white dark:bg-warm-gray-900 text-warm-gray-900 dark:text-white text-sm disabled:opacity-60';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => !submitting && onClose()}>
      <div className="bg-white dark:bg-warm-gray-800 rounded-xl shadow-xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-semibold text-warm-gray-900 dark:text-white mb-4">{t('bulkCodes.title')}</h3>

        {error && (
          <div className="mb-3 px-3 py-2 rounded bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-xs">
            {error}
          </div>
        )}
        {progress && (
          <div className="mb-3 px-3 py-2 rounded bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 text-xs">
            {progress}
          </div>
        )}

        <div className="mb-3">
          <label className="block text-xs font-semibold text-warm-gray-600 dark:text-warm-gray-400 mb-1">{t('bulkCodes.rangeLabel')}</label>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <span className="block text-[11px] text-warm-gray-500 mb-0.5">{t('bulkCodes.fromLabel')}</span>
              <input
                type="number"
                min={1}
                value={fromIdx}
                onChange={e => setFromIdx(Math.max(1, Math.floor(Number(e.target.value) || 1)))}
                disabled={submitting}
                className={inputCls}
              />
            </div>
            <div>
              <span className="block text-[11px] text-warm-gray-500 mb-0.5">{t('bulkCodes.toLabel')}</span>
              <input
                type="number"
                min={1}
                value={toIdx}
                onChange={e => setToIdx(Math.max(1, Math.floor(Number(e.target.value) || 1)))}
                disabled={submitting}
                className={inputCls}
              />
            </div>
          </div>
          <p className="text-xs text-warm-gray-500 mt-1">
            {t('bulkCodes.totalCount', { count })} · {t('bulkCodes.estimate', { seconds: estSeconds })}
          </p>
          <p className="text-[11px] text-warm-gray-400 mt-0.5">{t('bulkCodes.rangeHelp')}</p>
        </div>

        <div className="mb-3">
          <label className="block text-xs font-semibold text-warm-gray-600 dark:text-warm-gray-400 mb-1">{t('bulkCodes.nameTemplateLabel')}</label>
          <input
            type="text"
            value={nameTemplate}
            onChange={e => setNameTemplate(e.target.value)}
            disabled={submitting}
            placeholder="SAS-Member-XXX"
            className={inputCls}
          />
        </div>

        <div className="mb-3">
          <label className="block text-xs font-semibold text-warm-gray-600 dark:text-warm-gray-400 mb-1">{t('bulkCodes.emailTemplateLabel')}</label>
          <input
            type="text"
            value={emailTemplate}
            onChange={e => setEmailTemplate(e.target.value)}
            disabled={submitting}
            placeholder="sas-member-XXX@gmail.com"
            className={inputCls}
          />
        </div>

        <div className="mb-3">
          <label className="block text-xs font-semibold text-warm-gray-600 dark:text-warm-gray-400 mb-1">{t('bulkCodes.notesTemplateLabel')}</label>
          <input
            type="text"
            value={notesTemplate}
            onChange={e => setNotesTemplate(e.target.value)}
            disabled={submitting}
            placeholder="SAS-Member-XXX"
            className={inputCls}
          />
          <p className="text-[11px] text-warm-gray-400 mt-1">{t('bulkCodes.templateHelp')}</p>
        </div>

        {rangeOk && (previewFirst || previewLast) && (
          <div className="mb-4 px-3 py-2 rounded bg-warm-gray-50 dark:bg-warm-gray-900 border border-warm-gray-200 dark:border-warm-gray-700 text-xs text-warm-gray-700 dark:text-warm-gray-300">
            <span className="font-semibold mr-2">{t('bulkCodes.previewLabel')}:</span>
            <span className="font-mono">{t('bulkCodes.previewRange', { first: previewFirst, last: previewLast })}</span>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button onClick={onClose} disabled={submitting}
            className="px-4 py-2 rounded border border-warm-gray-300 dark:border-warm-gray-600 text-warm-gray-700 dark:text-warm-gray-300 text-sm disabled:opacity-50">
            {t('common.cancel')}
          </button>
          <button onClick={() => void submit()} disabled={submitting || !rangeOk}
            className="px-4 py-2 rounded bg-blue-500 hover:bg-blue-600 text-white text-sm font-semibold disabled:opacity-50">
            {submitting ? t('bulkCodes.submitting') : t('bulkCodes.submit', { count })}
          </button>
        </div>
      </div>
    </div>
  );
}
